/**
 * Highest / lowest GHG by sector: per capita and total, for California cities or counties.
 * Layout mirrors Overview Climate Equity Rankings (2023).
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  PUBLICATION_GHG_CHART_YEAR_MIN,
  PUBLICATION_GHG_SPATIAL_YEAR_MAX,
} from '../../config/publicationGhgChartYears';
import { PUBLICATION_ANALYSIS_CHART_IDS } from '../../config/publicationAnalysisChartIds';
import { ChartPngPageIdOverrideContext, useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { COUNTY_GHG_MAP_METRIC_OPTIONS, type CountyGhgMapMetric } from '../../services/countyPerCapitaGhgMapService';
import {
  fetchGhgRankingPlaces,
  perCapitaMtForRankingRow,
  totalMtForRankingRow,
  type GhgRankingGeography,
  type GhgRankingPlaceRow,
} from '../../services/ghgEmissionsRankingsService';
import { BERKELEY_BRAND } from '../../theme/navChartPalette';
import { downloadDataAsCSV, downloadDomAsPNG, formatFilename } from '../../utils/downloadUtils';
import { PublicationFigureDownloads } from '../publication/PublicationFigureDownloads';

export interface GhgEmissionsRankingsTableProps {
  /** Current app location name — row highlight when it matches a table name */
  location?: string;
}

type RankedRow = GhgRankingPlaceRow & { displayRank: number };

type RowsPerTable = 5 | 10 | 25 | 50 | 100 | 'all';

const ROWS_PER_TABLE_OPTIONS: RowsPerTable[] = [5, 10, 25, 50, 100, 'all'];

function rowsPerTableToLimit(n: RowsPerTable): number {
  return n === 'all' ? Number.POSITIVE_INFINITY : n;
}

function formatMt(mt: number): string {
  return `${Math.round(mt).toLocaleString()}`;
}

function formatPerCapita(tPerPerson: number): string {
  if (tPerPerson >= 100) return `${tPerPerson.toFixed(1)}`;
  if (tPerPerson >= 10) return `${tPerPerson.toFixed(2)}`;
  return `${tPerPerson.toFixed(3)}`;
}

function assignRanks(
  rows: GhgRankingPlaceRow[],
  metric: CountyGhgMapMetric,
  mode: 'perCapita' | 'total',
  descending: boolean,
  limit: number
): RankedRow[] {
  const scored = rows
    .map((r) => {
      const v =
        mode === 'perCapita' ? perCapitaMtForRankingRow(r, metric) : totalMtForRankingRow(r, metric);
      return { r, v };
    })
    .filter((x): x is { r: GhgRankingPlaceRow; v: number } => x.v != null && Number.isFinite(x.v) && x.v > 0);

  scored.sort((a, b) => (descending ? b.v - a.v : a.v - b.v));
  return scored.slice(0, limit).map((x, i) => ({ ...x.r, displayRank: i + 1 }));
}

const TABLE_TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
const TABLE_TH_RIGHT: React.CSSProperties = { ...TABLE_TH, textAlign: 'right' };
const TABLE_TD: React.CSSProperties = {
  padding: '6px',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};
const TABLE_TD_RIGHT: React.CSSProperties = { ...TABLE_TD, textAlign: 'right', fontWeight: 600 };

export const GhgEmissionsRankingsTable: React.FC<GhgEmissionsRankingsTableProps> = ({ location }) => {
  const figureRef = useRef<HTMLDivElement>(null);
  const pngPageOverride = useContext(ChartPngPageIdOverrideContext);
  const pngPageId = pngPageOverride ?? 'statewide_analysis';
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  const [year, setYear] = useState<number>(PUBLICATION_GHG_SPATIAL_YEAR_MAX);
  const [geographyType, setGeographyType] = useState<GhgRankingGeography>('County');
  const [metric, setMetric] = useState<CountyGhgMapMetric>('total');
  const [rowsPerTable, setRowsPerTable] = useState<RowsPerTable>(5);
  const [rows, setRows] = useState<GhgRankingPlaceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchGhgRankingPlaces(year, geographyType);
    if (res.error) {
      setRows([]);
      setError(res.error);
    } else {
      setRows(res.rows);
    }
    setLoading(false);
  }, [year, geographyType]);

  useEffect(() => {
    void load();
  }, [load]);

  const rowLimit = rowsPerTableToLimit(rowsPerTable);

  const { topPc, bottomPc, topTot, bottomTot } = useMemo(() => {
    return {
      topPc: assignRanks(rows, metric, 'perCapita', true, rowLimit),
      bottomPc: assignRanks(rows, metric, 'perCapita', false, rowLimit),
      topTot: assignRanks(rows, metric, 'total', true, rowLimit),
      bottomTot: assignRanks(rows, metric, 'total', false, rowLimit),
    };
  }, [rows, metric, rowLimit]);

  const locLower = location?.trim().toLowerCase() ?? '';
  const rowHighlight = (name: string) =>
    locLower.length > 0 && name.trim().toLowerCase() === locLower;

  const yearOptions = useMemo(() => {
    const o: number[] = [];
    for (let y = PUBLICATION_GHG_CHART_YEAR_MIN; y <= PUBLICATION_GHG_SPATIAL_YEAR_MAX; y++) o.push(y);
    return o;
  }, []);

  const selectStyle: React.CSSProperties = {
    border: '1px solid var(--border-color, #cbd5e1)',
    borderRadius: '6px',
    padding: '4px 8px',
    background: 'var(--card-bg, #fff)',
    color: 'var(--text-primary, #0f172a)',
  };

  const metricLabel = COUNTY_GHG_MAP_METRIC_OPTIONS.find((o) => o.value === metric)?.label ?? metric;

  const handleDownloadCsv = useCallback(() => {
    if (loading || error || !rows.length) return;
    const out: Record<string, unknown>[] = [];
    const push = (tableTitle: string, subtitle: string, mode: 'per_capita' | 'total', data: RankedRow[]) => {
      for (const row of data) {
        const pc = perCapitaMtForRankingRow(row, metric);
        const tot = totalMtForRankingRow(row, metric);
        out.push({
          table_title: tableTitle,
          table_subtitle: subtitle,
          rank: row.displayRank,
          geography_type: geographyType,
          place_name: row.location_name,
          geofips: row.geofips,
          year,
          metric,
          metric_label: metricLabel,
          mode,
          per_capita_mt_co2e: pc != null && Number.isFinite(pc) ? pc : '',
          total_mt_co2e: tot != null && Number.isFinite(tot) ? tot : '',
        });
      }
    };
    push('Highest per capita', 'Most sector emissions per resident', 'per_capita', topPc);
    push('Lowest per capita', 'Least sector emissions per resident', 'per_capita', bottomPc);
    push('Largest total', 'Highest sector total in inventory', 'total', topTot);
    push('Smallest total', 'Lowest sector total in inventory', 'total', bottomTot);
    if (!out.length) return;
    downloadDataAsCSV(out, formatFilename('California', 'GHG_Rankings'));
  }, [
    loading,
    error,
    rows.length,
    metric,
    metricLabel,
    geographyType,
    year,
    topPc,
    bottomPc,
    topTot,
    bottomTot,
  ]);

  const handleDownloadPng = useCallback(() => {
    return downloadDomAsPNG(figureRef.current, formatFilename('California', 'GHG_Rankings'));
  }, []);

  useEffect(() => {
    const id = PUBLICATION_ANALYSIS_CHART_IDS.rankings;
    const label = 'GHG emissions rankings tables';
    registerPng(id, label, () => void handleDownloadPng(), pngPageId);
    return () => unregisterPng(id);
  }, [registerPng, unregisterPng, handleDownloadPng, pngPageId]);

  const renderTable = (
    title: string,
    subtitle: string,
    data: RankedRow[],
    valueHeader: string,
    formatCell: (r: GhgRankingPlaceRow) => string,
    titleColor: string
  ) => (
    <div>
      <h4 className="publication-ghg-rankings-block-title" style={{ margin: '0 0 4px 0', color: titleColor }}>
        {title}
      </h4>
      <p className="publication-ghg-rankings-block-sub" style={{ margin: '0 0 8px 0', fontSize: '0.8rem', color: '#64748b' }}>
        {subtitle}
      </p>
      <table className="publication-ghg-rankings-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: 'var(--section-bg-alt, #f8fafc)' }}>
            <th style={TABLE_TH}>#</th>
            <th style={TABLE_TH}>{geographyType === 'City' ? 'City' : 'County'}</th>
            <th style={TABLE_TH_RIGHT}>{valueHeader}</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={3} style={{ ...TABLE_TD, color: '#64748b' }}>
                No rows with positive values for this measure.
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={`${title}-${row.geofips}`}
                style={{
                  backgroundColor: rowHighlight(row.location_name) ? 'var(--header-button-hover-bg, #e2e8f0)' : 'transparent',
                }}
              >
                <td style={TABLE_TD}>{row.displayRank}</td>
                <td style={TABLE_TD}>{row.location_name}</td>
                <td style={TABLE_TD_RIGHT}>{formatCell(row)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="publication-ghg-rankings publication-figure-frame" ref={figureRef}>
      <div className="publication-ghg-rankings-header">
        <h3 className="publication-ghg-rankings-title">GHG emissions rankings</h3>
        <div className="publication-ghg-rankings-controls">
          <label className="publication-scatter-field">
            <span>Sector</span>
            <select value={metric} onChange={(e) => setMetric(e.target.value as CountyGhgMapMetric)} style={selectStyle} disabled={loading}>
              {COUNTY_GHG_MAP_METRIC_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="publication-scatter-field">
            <span>Geography</span>
            <select
              value={geographyType}
              onChange={(e) => setGeographyType(e.target.value as GhgRankingGeography)}
              style={selectStyle}
              disabled={loading}
            >
              <option value="County">Counties</option>
              <option value="City">Cities</option>
            </select>
          </label>
          <label className="publication-scatter-field">
            <span>Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={selectStyle} disabled={loading}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="publication-scatter-field">
            <span>Rows per table</span>
            <select
              value={rowsPerTable === 'all' ? 'all' : String(rowsPerTable)}
              onChange={(e) => {
                const v = e.target.value;
                setRowsPerTable(v === 'all' ? 'all' : (Number(v) as RowsPerTable));
              }}
              style={selectStyle}
              disabled={loading}
              aria-label="Rows per rankings table"
            >
              {ROWS_PER_TABLE_OPTIONS.map((n) => (
                <option key={n} value={n === 'all' ? 'all' : n}>
                  {n === 'all' ? 'All' : n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <p className="publication-ghg-rankings-lede">
        Highest / lowest by selected measure and year. Per capita = sector total ÷ population.
      </p>
      {loading && <p className="publication-county-ghg-status">Loading rankings…</p>}
      {error && !loading && <p className="publication-scatter-error">{error}</p>}
      {!loading && !error && (
        <>
          <h4 className="publication-ghg-rankings-section-label">Per capita (metric tons CO₂e / person)</h4>
          <div className="publication-ghg-rankings-grid">
            {renderTable(
              'Highest per capita',
              'Most sector emissions per resident',
              topPc,
              't / person',
              (r) => {
                const v = perCapitaMtForRankingRow(r, metric);
                return v != null && Number.isFinite(v) ? formatPerCapita(v) : '—';
              },
              BERKELEY_BRAND.goldenGate
            )}
            {renderTable(
              'Lowest per capita',
              'Least sector emissions per resident',
              bottomPc,
              't / person',
              (r) => {
                const v = perCapitaMtForRankingRow(r, metric);
                return v != null && Number.isFinite(v) ? formatPerCapita(v) : '—';
              },
              BERKELEY_BRAND.beanMillennial
            )}
          </div>
          <h4 className="publication-ghg-rankings-section-label">Total (metric tons CO₂e)</h4>
          <div className="publication-ghg-rankings-grid">
            {renderTable(
              'Largest total',
              'Highest sector total in inventory',
              topTot,
              'MT',
              (r) => formatMt(totalMtForRankingRow(r, metric)),
              BERKELEY_BRAND.goldenGate
            )}
            {renderTable(
              'Smallest total',
              'Lowest sector total in inventory',
              bottomTot,
              'MT',
              (r) => formatMt(totalMtForRankingRow(r, metric)),
              BERKELEY_BRAND.beanMillennial
            )}
          </div>
        </>
      )}
      <PublicationFigureDownloads
        onCsv={handleDownloadCsv}
        onPng={handleDownloadPng}
        csvDisabled={loading || !!error || !rows.length}
        pngDisabled={loading || !!error}
        ariaLabel="Download GHG rankings figure"
      />
    </div>
  );
};

export default GhgEmissionsRankingsTable;
